﻿package org.example.project.registrierung

import androidx.compose.runtime.Composable
import org.example.project.dashboard.DashboardTheme
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.example.project.utils.DashboardVerlaufHintergrund
import org.example.project.settings.LogoSettings
import org.example.project.login.UnifiedAccessStyles
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private object TabletRegistrierungStyles : StyleSheet() {

    // Animations
    private val topBarDrop by keyframes {
        from { opacity(0); property("transform", "translateY(-22px)") }
        to   { opacity(1); property("transform", "translateY(0)") }
    }
    private val bgReveal by keyframes {
        from { opacity(0.15); property("filter", "blur(18px)") }
        to   { opacity(0.9);  property("filter", "blur(0)") }
    }

    // TopBar
    val topBar by style {
        position(Position.Fixed); top(0.px); left(0.px); right(0.px)
        width(100.percent); height(60.px); padding(0.px, 16.px)
        property("z-index", "100")
        display(DisplayStyle.Flex); alignItems(AlignItems.Center)
        backgroundColor(Color.transparent)
        border(0.px)
        boxSizing("border-box")
        opacity(0)
        property("animation", "${topBarDrop.name} 420ms ease-out 80ms forwards")
    }
    val topBarLogo by style {
        position(Position.Absolute); left(50.percent)
        property("transform", "translateX(-50%)")
        height(34.px); property("object-fit", "contain"); cursor("pointer")
    }
    val topBarLanguage by style {
        width(176.px)
        flexShrink(0)
    }
    val topBarLanguageFlag by style {
        width(28.px)
        height(28.px)
        property("object-fit", "contain")
        flexShrink(0)
    }
    val topBarLanguageText by style {
        property("display", "inline-flex")
        alignItems(AlignItems.Center)
        height(28.px)
        lineHeight("1")
        whiteSpace("nowrap")
    }
    val topBarLanguageFlash by style {
        color(UISettings.ACCENT)
        property("text-shadow", "0 0 12px ${UISettings.ACCENT}")
    }
    val tabLabel by style {
        property("display", "inline-flex")
        alignItems(AlignItems.Center)
        height(28.px)
        lineHeight("1")
        whiteSpace("nowrap")
        position(Position.Relative)
    }
    val tabLabelActive by style {
        property("display", "inline-flex")
        alignItems(AlignItems.Center)
        height(28.px)
        lineHeight("1")
        whiteSpace("nowrap")
        position(Position.Relative)
    }
    val tabLabelUnderline by style {
        position(Position.Absolute)
        left(0.px)
        right(0.px)
        bottom(2.px)
        height(1.px)
        backgroundColor(UISettings.ACCENT)
        property("pointer-events", "none")
    }
    val tabBtn by style {
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        gap(8.px); padding(0.px, 14.px); height(100.percent)
        fontSize(15.px); fontWeight("600"); cursor("pointer")
        property("transition", "all 0.2s"); property("user-select", "none"); opacity(0.85)
        hover { opacity(1) }
    }
    val tabActive by style {
        color(UISettings.ACCENT)
        property("text-decoration", "none")
    }
    val tabInactive by style {
        color(Color("#8a8f9a"))
        property("text-decoration", "none")
        hover { color(Color.white) }
    }
    val languagePopupLayer by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "1000")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }
    val languagePopupMenu by style {
        width(156.px)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        borderRadius(10.px)
        property("box-shadow", "0 18px 40px ${DashboardTheme.SURFACE_SHADOW}")
        property("backdrop-filter", "blur(18px)")
        maxHeight(360.px)
        overflowY("auto")
        overflowX("hidden")
    }
    val languagePopupOption by style {
        minHeight(72.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(6.px)
        padding(8.px, 12.px)
        boxSizing("border-box")
        color(DashboardTheme.TEXT_LIGHT)
        cursor("pointer")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.08)"))
        }
    }
    val languagePopupOptionActive by style {
        property("background", "rgba(255,0,0,0.10)")
    }

    // Main content area
    val mainContent by style {
        display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
        width(100.vw); height(100.vh)
        backgroundColor(Color.transparent); overflow("hidden")
        paddingTop(0.px); paddingBottom(64.px); boxSizing("border-box")
    }

    // Hero image wrapper
    val heroWrapper by style {
        width(100.percent)
        display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
    }
    val heroImage by style {
        property("object-fit", "contain"); opacity(0.9)
        property("animation", "${bgReveal.name} 720ms ease-out forwards")
    }

    // Glowing divider
    val glowDivider by style {
        width(3.px); minWidth(3.px); height(550.px); flexShrink(0)
        opacity(1); position(Position.Relative); property("z-index", "2")
        borderRadius(999.px)
        property("background",
            "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.28) 18%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0.28) 82%, rgba(255,255,255,0.06) 100%)")
        property("box-shadow", "0 0 24px rgba(255,255,255,0.55), 0 0 10px rgba(255,255,255,0.35)")
    }

    // Form panel
    val formPanel by style {
        padding(40.px); backgroundColor(Color.transparent)
        border(0.px); borderRadius(20.px)
        textAlign("center"); color(Color.white)
        property("backdrop-filter", "blur(15px)"); property("z-index", "200")
        boxSizing("border-box"); position(Position.Relative)
        display(DisplayStyle.Flex); property("flex-direction", "column")
        justifyContent(JustifyContent.FlexStart)
    }

    // Form internals
    val formInput by style {
        width(100.percent); minWidth(0.px); height(46.px); padding(0.px, 16.px)
        borderRadius(8.px); backgroundColor(Color.transparent); color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.18)")
        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)")
        boxSizing("border-box"); fontSize(15.px)
        focus { property("border-color", "#ffffff"); outline("none") }
    }
    val formInputError by style { property("border-color", "#ff4d4d") }
    val primaryButton by style {
        width(100.percent); maxWidth(500.px)
        property("margin-left", "auto"); property("margin-right", "auto")
        marginTop(22.px); height(50.px)
        backgroundColor(Color.transparent); color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.18)"); borderRadius(8.px)
        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)")
        cursor("pointer"); fontSize(15.px); fontWeight("600")
        property("transition", "opacity 0.15s")
        disabled { opacity(0.4); cursor("not-allowed") }
    }
    val formContainer by style {
        width(100.percent); maxWidth(500.px)
        property("margin-left", "auto"); property("margin-right", "auto")
        display(DisplayStyle.Flex); property("flex-direction", "column")
        flex(1)
        minHeight(0.px)
        gap(14.px); marginTop(22.px); boxSizing("border-box")
    }
    val registrationFormContainer by style {
        width(100.percent); maxWidth(500.px)
        property("margin-left", "auto"); property("margin-right", "auto")
        display(DisplayStyle.Flex); property("flex-direction", "column")
        flex(1)
        minHeight(0.px)
        gap(14.px); marginTop(0.px); boxSizing("border-box")
        justifyContent(JustifyContent.Center)
        paddingBottom(0.px)
    }
    val inlineRow by style {
        width(100.percent); display(DisplayStyle.Flex); gap(12.px); boxSizing("border-box")
    }
    val sectionTitle by style {
        color(Color.white); marginTop(0.px); marginBottom(4.px)
    }
    val sectionSubtitle by style {
        color(Color("#9aa0a6")); fontSize(14.px); opacity(0.8)
        marginTop(0.px); marginBottom(4.px)
    }
    val errorText by style {
        fontSize(11.px); color(Color("#ff4d4d")); opacity(0.9)
        whiteSpace("nowrap"); property("pointer-events", "none")
    }
    val backArrow by style {
        position(Position.Absolute); top(32.px); left(25.px)
        width(28.px); height(28.px); cursor("pointer"); opacity(0.5)
        hover { opacity(1) }
        property("transition", "opacity 0.15s")
    }

    // Step-1 role grid
    val roleGrid by style {
        display(DisplayStyle.Flex); gap(24.px); marginTop(20.px)
    }
    val roleCard by style {
        flex(1); padding(28.px); borderRadius(14.px)
        backgroundColor(Color.transparent)
        property("border", "1px solid #2a2d34"); cursor("pointer")
        property("transition", "transform 0.2s ease, border-color 0.2s ease")
        hover { property("border-color", "#4a4f59") }
    }

}

// ─────────────────────────────────────────────────────────────────────────────
// Slot-Implementierung für Tablet
// ─────────────────────────────────────────────────────────────────────────────

private fun buildTabletSlots(registrationLayout: Boolean): FahrerUiSlots = FahrerUiSlots(

    textInput = { type, ph, value, error, onInput, onEnter, layout ->
        Input(type, attrs = {
            classes(TabletRegistrierungStyles.formInput)
            if (error) classes(TabletRegistrierungStyles.formInputError)
            placeholder(ph)
            this.value(value)
            onInput { onInput(it.value) }
            if (onEnter != null) onKeyDown { if (it.key == "Enter") { it.preventDefault(); onEnter() } }
            layout?.invoke(this)
        })
    },

    primaryButton = { label, enabled, loading, onClick ->
        Button(attrs = {
            classes(TabletRegistrierungStyles.primaryButton)
            if (registrationLayout) {
                style {
                    position(Position.Absolute)
                    left(28.px)
                    right(28.px)
                    bottom(72.px)
                    property("width", "auto")
                    marginTop(0.px)
                }
            }
            if (!enabled || loading) disabled()
            onClick { onClick() }
        }) { Text(label) }
    },

    inlineRow = { content ->
        Div({ classes(TabletRegistrierungStyles.inlineRow) }) { content() }
    },

    formContainer = { content ->
        Div({
            classes(
                if (registrationLayout) TabletRegistrierungStyles.registrationFormContainer
                else TabletRegistrierungStyles.formContainer
            )
        }) { content() }
    },

    sectionTitle = { title, subtitle ->
        H2({
            classes(TabletRegistrierungStyles.sectionTitle)
            if (!registrationLayout) style { marginBottom(22.px) }
        }) { Text(title) }
        if (title != LocalizationManager.get("driver_registration_step_language_title")) {
            P({
                classes(TabletRegistrierungStyles.sectionSubtitle)
                if (!registrationLayout) style { marginBottom(0.px) }
            }) { Text(subtitle) }
        }
    },

    errorText = { msg ->
        Span({ classes(TabletRegistrierungStyles.errorText) }) { Text(msg) }
    },

    backArrow = { onClick ->
        Img(src = "images/back.svg", attrs = {
            classes(TabletRegistrierungStyles.backArrow)
            onClick { onClick() }
        })
    },

    languagePlaceholder = { selectedLanguage, onLanguage ->
        Div({
            classes(TabletRegistrierungStyles.roleGrid)
            style {
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(10.px)
                width(100.percent)
            }
        }) {
            // LKW-Bühne
            Div({
                style {
                    width(100.percent)
                    minHeight(0.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                Img(src = "images/fahrer.png", attrs = {
                    style {
                        width(100.percent); height(190.px)
                    property("object-fit", "contain")
                        display(DisplayStyle.Block)
                    }
                })
            }
            FahrerLanguageDropdown(selectedLanguage, onLanguage)
        }
    },
)

@Composable
internal fun FahrerRegistrationLoginSwitch(onLogin: () -> Unit, desktopLayout: Boolean = false) {
    Div({
        classes(UnifiedAccessStyles.switchRow)
        style {
            width(100.percent)
            color(Color.white)
            textAlign("center")
            flexWrap(FlexWrap.Wrap)
            if (desktopLayout) {
                position(Position.Absolute)
                property("width", "auto")
                left(28.px)
                right(28.px)
                bottom(28.px)
            }
        }
    }) {
        Span({ style { color(Color.white) } }) { Text(LocalizationManager.get("driver_already_member")) }
        Span({
            classes(UnifiedAccessStyles.switchAction)
            style { color(Color.white) }
            onClick { onLogin() }
        }) { Text(LocalizationManager.get("driver_login_button")) }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tablet-Layout
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun TabletFahrerRegistrierungLayout(
    activeView: String,
    loginState: FahrerLoginFlowState,
    regState: FahrerRegFlowState,
    loginCallbacks: FahrerLoginCallbacks,
    regCallbacks: FahrerRegCallbacks,
    registeredCompanies: List<All>,
    onSetView: (String) -> Unit,
    companionMode: Boolean = false,
) {
    Style(TabletRegistrierungStyles)
    Style(NetzwerkLoginStyles)
    Style(UnifiedAccessStyles)
    Style(object : StyleSheet() {
        init {
            "html, body" style {
                margin(0.px); padding(0.px)
                width(100.percent); height(100.percent)
                overflow("hidden")
            }
        }
    })
    DashboardVerlaufHintergrund()

    val slots = buildTabletSlots(activeView == "REGISTER")
    // ── TopBar ────────────────────────────────────────────────────────────────
    if (activeView == "NONE") Header({ classes(TabletRegistrierungStyles.topBar) }) {
        Img(src = LogoSettings.fahrerLoginHeroLogo, attrs = { style { height(48.px) } })
    }

    // ── Main Content ──────────────────────────────────────────────────────────
    Main({
        classes(TabletRegistrierungStyles.mainContent)
        if (companionMode) classes(UnifiedAccessStyles.stage)
    }) {
        Div({
            classes(TabletRegistrierungStyles.heroWrapper)
            if (activeView != "NONE") style {
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                gap(14.px)
            }
        }) {

            if (activeView == "NONE") {
                Img(src = "images/fahrer.png", attrs = {
                    classes(TabletRegistrierungStyles.heroImage)
                    style {
                        width(72.vw)
                        height(72.vh)
                    }
                })
            }

            if (activeView != "NONE") {
                Div({
                    classes(TabletRegistrierungStyles.formPanel)
                    if (activeView == "REGISTER") classes(UnifiedAccessStyles.panel)
                    if (activeView == "REGISTER") style {
                        width(700.px)
                        property("height", "calc(100vh - 110px)")
                        maxHeight(820.px)
                        if (regState.step == 1) overflow("hidden") else overflowY("auto")
                        padding(28.px)
                        justifyContent(JustifyContent.FlexStart)
                    }
                }) {
                    if (activeView == "REGISTER") Img(
                        src = LogoSettings.desktopRegistrationHeaderLogo,
                        attrs = {
                            classes(UnifiedAccessStyles.logo)
                            style { flexShrink(0) }
                        }
                    )
                    when (activeView) {
                        "LOGIN"    -> FahrerLoginFlow(loginState, loginCallbacks, slots, if (companionMode) null else { { onSetView("REGISTER") } })
                        "REGISTER" -> {
                            FahrerRegFlow(regState, regCallbacks, slots, desktopLayout = true) { onSetView("LOGIN") }
                        }
                    }
                }
            }
        }
    }

}
